import React, { useState } from 'react';
import { RefreshCw, ChevronLeft, ChevronRight, Loader2, Plus, Search, Trash2, Edit2, X } from 'lucide-react';

const DocumentTable = ({ 
  collection, 
  documents, 
  loading, 
  onRefresh, 
  onPageChange,
  onCreateDocument,
  onEditDocument,
  onDeleteDocument,
  onSearch,
  isReadOnly 
}) => {
  const currentPage = documents?.page || 1;
  const pageSize = documents?.pageSize || 20;
  const totalPages = documents?.totalPages || 1;
  const documentList = documents?.documents || [];
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const renderValue = (value) => {
    if (value === null) return <span className="text-ui-text-secondary italic">null</span>;
    if (value === undefined) return <span className="text-ui-text-secondary italic">undefined</span>;
    if (typeof value === 'object' && value.$oid) return value.$oid;
    if (typeof value === 'object') return JSON.stringify(value);
    if (typeof value === 'boolean') return value ? 'true' : 'false';
    return String(value);
  };

  const getDocumentFields = () => {
    if (!documentList.length) return [];
    // Get all unique fields from all documents
    const fieldSet = new Set();
    documentList.forEach(doc => {
      Object.keys(doc).forEach(key => fieldSet.add(key));
    });
    return Array.from(fieldSet);
  };

  const fields = getDocumentFields();

  return (
    <div className="h-full flex flex-col bg-white dark:bg-neutral-800">
      {/* Header */}
      <div className="border-b border-neutral-200 dark:border-neutral-700">
        <div className="p-4 flex items-center justify-between">
          <h3 className="text-sm font-medium text-ui-text-primary">{collection.name}</h3>
          
          <div className="flex items-center gap-1.5">
            {/* Action buttons */}
            <button
              onClick={() => setShowSearch(!showSearch)}
              className="p-0.5 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors"
              title="Search documents"
            >
              <Search size={14} />
            </button>
            <button
              onClick={onCreateDocument}
              disabled={isReadOnly}
              className="p-0.5 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title={isReadOnly ? 'Upgrade to create documents' : 'Create new document'}
            >
              <Plus size={14} />
            </button>
            
            {/* Pagination */}
            {documentList.length > 0 && (
              <>
                <div className="mx-2 h-3 w-px bg-neutral-200 dark:bg-neutral-700" />
                <div className="flex items-center gap-3">
                  <div className="text-sm text-ui-text-secondary">
                    Page {documents?.page || 1} of {totalPages}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onPageChange && onPageChange(currentPage - 1)}
                      disabled={currentPage === 1 || loading}
                      className="p-0.5 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <button
                      onClick={() => onPageChange && onPageChange(currentPage + 1)}
                      disabled={currentPage === totalPages || loading}
                      className="p-0.5 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-700 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {/* Search bar */}
        {showSearch && (
          <div className="px-4 pb-3">
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' && onSearch) {
                    onSearch(searchQuery);
                  }
                }}
                placeholder="Search documents... (press Enter to search)"
                className="w-full px-3 pr-8 py-1.5 text-sm bg-neutral-50 dark:bg-neutral-900 rounded border border-neutral-200 dark:border-neutral-600 text-ui-text-primary placeholder-ui-text-secondary focus:outline-none focus:ring-2 focus:ring-neutral-300 dark:focus:ring-neutral-600 focus:border-transparent"
                autoFocus
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    if (onSearch) onSearch('');
                  }}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-0.5 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-200 dark:hover:bg-neutral-700 rounded transition-colors"
                  title="Clear search"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="h-full">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
                <tr>
                  <th className="w-20 text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap"></th>
                  <th className="text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap">Loading...</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="2" className="text-center py-20">
                    <Loader2 className="w-6 h-6 animate-spin text-ui-text-secondary mx-auto" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : documentList.length === 0 ? (
          <div className="h-full">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
                <tr>
                  <th className="w-20 text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap"></th>
                  <th className="text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap">_id</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td colSpan="2" className="text-center py-20 text-ui-text-secondary">
                    No documents in this collection
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
              <tr>
                <th className="w-20 text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap">
                  
                </th>
                {fields.map(field => (
                  <th
                    key={field}
                    className="text-left px-4 py-2 font-medium text-ui-text-primary whitespace-nowrap"
                  >
                    {field}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {documentList.map((doc, index) => (
                <tr
                  key={doc._id?.$oid || index}
                  className="border-b border-neutral-100 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors group"
                >
                  <td className="w-20 px-4 py-2 text-left">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEditDocument && onEditDocument(doc)}
                        disabled={isReadOnly}
                        className="p-1 text-ui-text-secondary hover:text-ui-text-primary hover:bg-neutral-100 dark:hover:bg-neutral-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to edit documents' : 'Edit document'}
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => onDeleteDocument && onDeleteDocument(doc)}
                        disabled={isReadOnly}
                        className="p-1 text-ui-text-secondary hover:text-red-600 dark:hover:text-red-400 hover:bg-neutral-100 dark:hover:bg-neutral-600 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title={isReadOnly ? 'Upgrade to delete documents' : 'Delete document'}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                  {fields.map(field => (
                    <td
                      key={field}
                      className="px-4 py-2 text-ui-text-primary max-w-xs truncate"
                      title={renderValue(doc[field])}
                    >
                      {renderValue(doc[field])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default DocumentTable;
